/**
 * 請求先住所の読み取り互換ヘルパー (v3.2.0)。
 *
 * MailContact.billingAddresses[] が真のソースだが、旧データ (postalCode/prefecture/address/buildingName 直下)
 * からの読み取りも互換シムで補完する。
 *
 * - 書込側 (顧客編集 UI) は常に billingAddresses[] に正規化された配列を書き、
 *   旧フィールドには「既定の請求先」を同期書込する (mailRender / 既存PDF経路の後方互換)。
 * - 読込側 (請求書/見積書 PDF / mailRender 等) は本ヘルパーを必ず経由する。
 */
import type { BillingAddress, DeliveryAddress, MailContact } from '../types/mail';

/**
 * 互換シム: billingAddresses が空または未定義なら、旧フィールドから 1 件の請求先を合成して返す。
 * 旧フィールドも未設定なら空配列。
 */
export function getBillingAddresses(
  contact: Pick<
    MailContact,
    'billingAddresses' | 'postalCode' | 'prefecture' | 'address' | 'buildingName'
  >,
): BillingAddress[] {
  if (contact.billingAddresses && contact.billingAddresses.length > 0) {
    return contact.billingAddresses;
  }
  const hasLegacy = Boolean(
    contact.postalCode || contact.prefecture || contact.address || contact.buildingName,
  );
  if (!hasLegacy) return [];
  return [
    {
      id: 'legacy-default',
      label: null,
      recipientName: null,
      postalCode: contact.postalCode ?? null,
      prefecture: contact.prefecture ?? null,
      address: contact.address ?? null,
      buildingName: contact.buildingName ?? null,
      phone: null,
      isDefault: true,
    },
  ];
}

/**
 * 既定の請求先住所を返す。見つからなければ null。
 * `isDefault: true` を優先、無ければ配列先頭。
 */
export function getDefaultBillingAddress(
  contact: Pick<
    MailContact,
    'billingAddresses' | 'postalCode' | 'prefecture' | 'address' | 'buildingName'
  >,
): BillingAddress | null {
  const list = getBillingAddresses(contact);
  if (list.length === 0) return null;
  return list.find((a) => a.isDefault) ?? list[0];
}

/**
 * 配列内で isDefault が常に最大 1 件になるよう正規化する。
 * 1件もなければ先頭を既定にする。複数あれば最後に true を立てたものだけ残す。
 */
export function normalizeBillingAddresses(
  list: BillingAddress[],
  preferredDefaultId?: string,
): BillingAddress[] {
  if (list.length === 0) return [];
  const defaultId =
    preferredDefaultId && list.some((a) => a.id === preferredDefaultId)
      ? preferredDefaultId
      : (list.find((a) => a.isDefault)?.id ?? list[0].id);
  return list.map((a) => ({ ...a, isDefault: a.id === defaultId }));
}

/**
 * 既定の納品先住所を返す。見つからなければ null。
 * `isDefault: true` を優先、無ければ配列先頭。
 */
export function getDefaultDeliveryAddress(
  contact: Pick<MailContact, 'deliveryAddresses'>,
): DeliveryAddress | null {
  const list = contact.deliveryAddresses ?? [];
  if (list.length === 0) return null;
  return list.find((a) => a.isDefault) ?? list[0];
}

/**
 * Quote/Invoice 発行時に「contact + addressId」を「請求先住所スナップショット」に解決する (v3.2.2)。
 *
 * 用途:
 *  - UI 側で billingAddressId / deliveryAddressId を Callable に渡す
 *  - サーバー側で本ヘルパーを使い、contact から ID 一致の住所を取得して Quote.billingAddressSnapshot に保存
 *
 * フォールバック:
 *  - addressId 未指定 → 既定の請求先（getDefaultBillingAddress）
 *  - addressId 指定だが該当無し → 同上（既定にフォールバック）
 *  - それも無ければ null
 */
export function resolveBillingAddressSnapshot(
  contact: Pick<
    MailContact,
    'billingAddresses' | 'postalCode' | 'prefecture' | 'address' | 'buildingName'
  >,
  addressId?: string | null,
): BillingAddress | null {
  if (addressId) {
    const found = (contact.billingAddresses ?? []).find((a) => a.id === addressId);
    if (found) return found;
  }
  return getDefaultBillingAddress(contact);
}

/**
 * Quote/Invoice 発行時に「contact + addressId」を「納品先住所スナップショット」に解決する (v3.2.2)。
 * billing と同じセマンティクス。addressId に明示的に `__NONE__` を渡すと「納品先なし」(null)。
 */
export function resolveDeliveryAddressSnapshot(
  contact: Pick<MailContact, 'deliveryAddresses'>,
  addressId?: string | null,
): DeliveryAddress | null {
  if (addressId === '__NONE__') return null;
  const list = contact.deliveryAddresses ?? [];
  if (addressId) {
    const found = list.find((a) => a.id === addressId);
    if (found) return found;
  }
  if (list.length === 0) return null;
  return list.find((a) => a.isDefault) ?? list[0];
}

/**
 * 顧客書込時の legacy フィールド同期用。既定の請求先から旧4フィールドを再生成する。
 */
export function buildLegacyAddressFields(
  list: BillingAddress[],
): Pick<MailContact, 'postalCode' | 'prefecture' | 'address' | 'buildingName'> {
  if (list.length === 0) {
    return { postalCode: null, prefecture: null, address: null, buildingName: null };
  }
  const def = list.find((a) => a.isDefault) ?? list[0];
  return {
    postalCode: def.postalCode ?? null,
    prefecture: def.prefecture ?? null,
    address: def.address ?? null,
    buildingName: def.buildingName ?? null,
  };
}
